// ---------------------------------------------------------------------------
// When to start the scripts the first paint doesn't need
// ---------------------------------------------------------------------------
//
// Two tiers. Our own analytics (PostHog) starts once the page has loaded and
// the main thread is idle. Marketing tags (GTM with GA4 inside it, LinkedIn
// Insight) wait longer: the visitor's first scroll, tap or key press, or a
// timeout after load for visitors who only read. On PSI mobile these tags were
// most of the 3.2 s of main-thread work.

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
};

function whenIdle(fn: () => void): void {
  const w = window as IdleWindow;
  // Safari has no requestIdleCallback.
  if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout: 2000 });
  else window.setTimeout(fn, 1);
}

/** Run fn once the page has loaded and the main thread is idle. */
export function afterLoad(fn: () => void): void {
  if (typeof window === "undefined") return;
  if (document.readyState === "complete") whenIdle(fn);
  else window.addEventListener("load", () => whenIdle(fn), { once: true });
}

const INTENT_EVENTS = ["scroll", "pointerdown", "keydown", "touchstart"];

/**
 * Run fn on the visitor's first interaction, or timeoutMs after load if there
 * is none. The script itself starts on idle, never inside the event handler,
 * so loading a tag can't delay the response to that first tap (INP).
 */
export function onIntentOrTimeout(fn: () => void, timeoutMs: number): void {
  if (typeof window === "undefined") return;
  let done = false;
  let timer: number | undefined;
  const run = () => {
    if (done) return;
    done = true;
    INTENT_EVENTS.forEach((e) => window.removeEventListener(e, run));
    window.clearTimeout(timer);
    whenIdle(fn);
  };
  INTENT_EVENTS.forEach((e) =>
    window.addEventListener(e, run, { once: true, passive: true }),
  );
  afterLoad(() => {
    if (!done) timer = window.setTimeout(run, timeoutMs);
  });
}
